import c from "classnames";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import { InputBox } from "../InputBox/InputBox";
import { Input } from "../Input/Input";
import { Icon } from "../Icon/Icon";
import { Text } from "../Text/Text";

import {
  isRangeReversed,
  parseTypedDate,
  toDay,
} from "./parseTypedDate";
import classes from "./DateRangePicker.module.css";

type DateRange = [Date | null, Date | null];
type Bound = "start" | "end";

export type DateRangePickerRef = {
  /**
   * Flush typed drafts into onChange when valid.
   * Returns false when a draft is invalid/incomplete or the range is reversed;
   * onChange is not called in that case.
   */
  commit: () => boolean;
};

type Props = {
  value?: DateRange;
  onChange: (range: DateRange) => void;
  placeholder?: string;
  disabled?: boolean;
  formatDate?: (date: Date) => string;
  /** Stretch both fields across a slide-in panel. Dashboard triggers stay compact. */
  fullWidth?: boolean;
};

const defaultFormatDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const isSameDay = (a: Date, b: Date): boolean => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const today = (): Date => toDay(new Date());

const INVALID_DATE_MESSAGE = "Enter a valid start and end date.";
const REVERSED_RANGE_MESSAGE = "Start date must be on or before end date.";

const formatBound = (
  date: Date | null,
  formatDate: (date: Date) => string,
): string => (date ? formatDate(date) : "");

export const DateRangePicker = forwardRef<DateRangePickerRef, Props>(
  (
    {
      value,
      onChange,
      disabled = false,
      formatDate = defaultFormatDate,
      fullWidth = false,
    },
    ref,
  ) => {
    const [calendarBound, setCalendarBound] = useState<Bound | null>(null);
    const [editingBound, setEditingBound] = useState<Bound | null>(null);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [startDate, endDate] = value || [null, null];
    const [startText, setStartText] = useState(() =>
      formatBound(startDate, formatDate),
    );
    const [endText, setEndText] = useState(() =>
      formatBound(endDate, formatDate),
    );

    const isCalendarOpen = calendarBound !== null;

    useEffect(() => {
      // Keep invalid drafts visible after blur so the inline error stays actionable.
      if (error) {
        return;
      }
      if (editingBound !== "start") {
        setStartText(formatBound(startDate, formatDate));
      }
      if (editingBound !== "end") {
        setEndText(formatBound(endDate, formatDate));
      }
    }, [startDate, endDate, editingBound, formatDate, error]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setCalendarBound(null);
        }
      };

      if (isCalendarOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isCalendarOpen]);

    const applyRange = (next: DateRange): boolean => {
      const [nextStart, nextEnd] = next;
      if (isRangeReversed(nextStart, nextEnd)) {
        setError(REVERSED_RANGE_MESSAGE);
        return false;
      }
      setError(null);
      onChange(next);
      return true;
    };

    const commitTexts = (
      nextStartText: string,
      nextEndText: string,
    ): boolean => {
      const parsedStart = parseTypedDate(nextStartText);
      const parsedEnd = parseTypedDate(nextEndText);

      if (parsedStart === "invalid" || parsedEnd === "invalid") {
        setError(INVALID_DATE_MESSAGE);
        return false;
      }

      return applyRange([parsedStart, parsedEnd]);
    };

    const commitDrafts = (): boolean => commitTexts(startText, endText);

    useImperativeHandle(
      ref,
      () => ({
        commit: commitDrafts,
      }),
      // Re-bind when drafts change so Apply flushes the text last typed.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- commit closes over latest drafts
      [startText, endText, onChange],
    );

    const handleCalendarChange = (selectedValue: Date | null) => {
      if (!selectedValue || !calendarBound) {
        return;
      }

      const selectedDay = toDay(selectedValue);
      const next: DateRange =
        calendarBound === "start"
          ? [selectedDay, endDate]
          : [startDate, selectedDay];

      if (!applyRange(next)) {
        return;
      }

      if (calendarBound === "start") {
        setStartText(formatDate(selectedDay));
      } else {
        setEndText(formatDate(selectedDay));
      }
      setCalendarBound(null);
    };

    const handleClearBound = (bound: Bound, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) {
        return;
      }
      const next: DateRange =
        bound === "start" ? [null, endDate] : [startDate, null];
      setError(null);
      if (bound === "start") {
        setStartText("");
      } else {
        setEndText("");
      }
      onChange(next);
    };

    const openCalendar = (bound: Bound, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) {
        return;
      }
      setCalendarBound((current) => (current === bound ? null : bound));
    };

    const handleTextChange = (bound: Bound, text: string) => {
      if (bound === "start") {
        setStartText(text);
      } else {
        setEndText(text);
      }
      if (error) {
        setError(null);
      }
    };

    const handleTextBlur = () => {
      setEditingBound(null);
      commitDrafts();
    };

    const handleTextKeyDown = (
      bound: Bound,
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (commitDrafts()) {
          (event.target as HTMLInputElement).blur();
        }
      }
      if (event.key === "Escape") {
        event.preventDefault();
        if (bound === "start") {
          setStartText(formatBound(startDate, formatDate));
        } else {
          setEndText(formatBound(endDate, formatDate));
        }
        setError(null);
        setCalendarBound(null);
        (event.target as HTMLInputElement).blur();
      }
    };

    const renderBoundField = (
      bound: Bound,
      date: Date | null,
      text: string,
      placeholder: string,
      ariaLabel: string,
    ) => (
      <div
        className={c(
          classes.field,
          calendarBound === bound && classes.fieldActive,
          error && classes.fieldError,
        )}
      >
        <InputBox
          wrap={false}
          rightSlot={
            <div className={classes.iconContainer}>
              {date && !disabled && (
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => handleClearBound(bound, event)}
                  className={classes.clearButton}
                  aria-label={`Clear ${ariaLabel.toLowerCase()}`}
                >
                  ×
                </button>
              )}
              <button
                type="button"
                className={classes.calendarButton}
                aria-label={`Open ${ariaLabel.toLowerCase()} calendar`}
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => openCalendar(bound, event)}
              >
                <Icon icon="faCalendar" className={classes.calendarIcon} />
              </button>
            </div>
          }
        >
          <Input
            value={text}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => {
              setEditingBound(bound);
              setCalendarBound(null);
            }}
            onChange={(event) => handleTextChange(bound, event.target.value)}
            onBlur={handleTextBlur}
            onKeyDown={(event) => handleTextKeyDown(bound, event)}
            style={{
              cursor: disabled ? "not-allowed" : "text",
              fontVariantNumeric: "tabular-nums",
              minWidth: 0,
            }}
            aria-label={ariaLabel}
            aria-invalid={Boolean(error)}
          />
        </InputBox>
      </div>
    );

    const calendarMinDate =
      calendarBound === "end" && startDate ? toDay(startDate) : undefined;
    const calendarMaxDate = (() => {
      const max = today();
      if (calendarBound === "start" && endDate) {
        const endDay = toDay(endDate);
        return endDay < max ? endDay : max;
      }
      return max;
    })();

    const calendarValue =
      calendarBound === "start" && startDate
        ? toDay(startDate)
        : calendarBound === "end" && endDate
          ? toDay(endDate)
          : null;

    const tileClassName = ({
      date,
      view,
    }: {
      date: Date;
      view: string;
    }): string | null => {
      if (view !== "month") {
        return null;
      }
      if (startDate && endDate) {
        const start = toDay(startDate);
        const end = toDay(endDate);
        if (isSameDay(date, start) && isSameDay(date, end)) {
          return classes.tileSingle;
        }
        if (isSameDay(date, start)) {
          return classes.tileRangeStart;
        }
        if (isSameDay(date, end)) {
          return classes.tileRangeEnd;
        }
        const time = toDay(date).getTime();
        if (time > start.getTime() && time < end.getTime()) {
          return classes.tileRangeBetween;
        }
      } else if (startDate && isSameDay(date, startDate)) {
        return classes.tileSingle;
      } else if (endDate && isSameDay(date, endDate)) {
        return classes.tileSingle;
      }
      return null;
    };

    return (
      <div
        ref={containerRef}
        className={c(classes.container, fullWidth && classes.fullWidth)}
      >
        <div className={classes.fields}>
          {renderBoundField("start", startDate, startText, "From", "Start date")}

          <span className={classes.separator} aria-hidden="true">
            –
          </span>

          {renderBoundField("end", endDate, endText, "To", "End date")}
        </div>

        {error ? (
          <div role="alert">
            <Text size={12} color="danger">
              {error}
            </Text>
          </div>
        ) : null}

        {isCalendarOpen && (
          <div className={classes.calendarContainer}>
            <Calendar
              onChange={(selectedValue) => {
                if (selectedValue instanceof Date) {
                  handleCalendarChange(selectedValue);
                }
              }}
              value={calendarValue}
              selectRange={false}
              minDate={calendarMinDate}
              maxDate={calendarMaxDate}
              tileClassName={tileClassName}
              className={classes.calendar}
            />
          </div>
        )}
      </div>
    );
  },
);

DateRangePicker.displayName = "DateRangePicker";
